// // CustomerManagerDashboard.tsx
// import React, { useState, useEffect } from "react";
// import {
//   Megaphone,
//   CalendarDays,
//   Package,
//   Image as ImageIcon,
//   Clock,
//   CheckCircle2,
//   TrendingUp,
//   ChevronRight,
//   PieChart,
//   Activity,
//   Sparkles,
// } from "lucide-react";
// import { useSelector } from "react-redux";
// import type { RootState } from "../../store/store";
// import { API_V1_BASE_URL } from "../../config/api";

// // ---- Types -----------------------------------------------------------

// interface CampaignStats {
//   total: number;
//   active: number;
//   archived: number;
// }

// interface EventStats {
//   total: number;
//   upcoming: number;
//   past: number;
// }

// interface ProductStats {
//   total: number;
//   active: number;
//   inactive: number;
// }

// interface AssetStats {
//   total: number;
//   ready: number;
//   processing: number;
// }

// interface MonthlyGrowthPoint {
//   month: string;
//   count: number;
// }

// interface DashboardStatsResponse {
//   campaigns: CampaignStats;
//   events: EventStats;
//   products: ProductStats;
//   assets: AssetStats;
//   campaignGrowth: MonthlyGrowthPoint[];
// }

// interface Campaign {
//   campaign_id: string;
//   title: string;
//   status: string;
//   start_date?: string;
//   end_date?: string;
//   updated_at?: string;
// }

// // ---- Component ---------------------------------------------------------

// const CustomerManagerDashboard: React.FC = () => {
//   const { token, accessToken, user } = useSelector((state: RootState) => state.auth);
//   const storedUser = (() => {
//     try {
//       return JSON.parse(localStorage.getItem("user") || "null");
//     } catch {
//       return null;
//     }
//   })();
//   const authToken = accessToken || token || localStorage.getItem("accessToken");
//   const tenantId =
//     (user as any)?.tenantId ||
//     (user as any)?.tenant_id ||
//     storedUser?.tenantId ||
//     storedUser?.tenant_id;

//   const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
//   const [recentCampaigns, setRecentCampaigns] = useState<Campaign[]>([]);
//   const [loading, setLoading] = useState<boolean>(true);
//   const [error, setError] = useState<string | null>(null);
//   const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);

//   const authHeaders = {
//     Authorization: `Bearer ${authToken || ""}`,
//     "Content-Type": "application/json",
//   };

//   useEffect(() => {
//     const fetchAll = async () => {
//       try {
//         setLoading(true);
//         setError(null);

//         if (!authToken) {
//           throw new Error("No authentication token found. Please login again.");
//         }
//         if (!tenantId) {
//           throw new Error("No tenant found for this account.");
//         }

//         const statsRes = await fetch(
//           `${API_V1_BASE_URL}/customer-manager/dashboard-stats?tenant_id=${tenantId}`,
//           { headers: authHeaders },
//         );

//         if (!statsRes.ok) {
//           const errData = await statsRes.json().catch(() => null);
//           throw new Error(errData?.message || "Failed to fetch dashboard stats");
//         }
//         const statsJson = await statsRes.json();
//         setStats(statsJson.data as DashboardStatsResponse);

//         // Recent campaigns are supplementary. A failure here must not prevent
//         // the manager dashboard statistics from rendering.
//         try {
//           const campaignsRes = await fetch(
//             `${API_V1_BASE_URL}/campaigns?tenant_id=${tenantId}&page=1&limit=5`,
//             { headers: authHeaders },
//           );
//           if (campaignsRes.ok) {
//             const campaignsJson = await campaignsRes.json();
//             setRecentCampaigns(
//               Array.isArray(campaignsJson.data) ? campaignsJson.data : [],
//             );
//           } else {
//             setRecentCampaigns([]);
//           }
//         } catch (campaignError) {
//           console.warn("Unable to load recent campaigns:", campaignError);
//           setRecentCampaigns([]);
//         }
//       } catch (err) {
//         setError(err instanceof Error ? err.message : "Failed to fetch data");
//         console.error("Error fetching customer manager dashboard:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAll();
//   }, [authToken, tenantId]);

//   const pct = (part: number, total: number) =>
//     total > 0 ? Math.round((part / total) * 100) : 0;

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
//         <div className="text-center">
//           <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
//           <p className="mt-4 text-gray-600 font-medium">Loading dashboard...</p>
//         </div>
//       </div>
//     );
//   }

//   if (error || !stats) {
//     return (
//       <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
//         <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
//           <div className="text-red-500 text-center">
//             <div className="text-5xl mb-4">⚠️</div>
//             <p className="text-xl font-semibold">Error Loading Dashboard</p>
//             <p className="mt-2 text-gray-600">{error || "No data available"}</p>
//             <button
//               onClick={() => window.location.reload()}
//               className="mt-6 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 shadow-md hover:shadow-lg"
//             >
//               Retry
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   const { campaigns, events, products, assets, campaignGrowth } = stats;
//   const maxGrowth = Math.max(...campaignGrowth.map((d) => d.count), 1);

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 p-6">
//       {/* Header */}
//       <div className="mb-8 animate-fadeIn">
//         <div className="flex items-center justify-between">
//           <div>
//             <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
//               Dashboard Overview
//             </h1>
//             <p className="text-gray-500 mt-1 flex items-center gap-2">
//               <Activity className="w-4 h-4" />
//               A summary of campaigns, events, products and assets for your
//               tenant
//             </p>
//           </div>
//           <span className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium flex items-center gap-2">
//             <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
//             Live
//           </span>
//         </div>
//       </div>

//       {/* Getting started: shows only the steps that are still pending */}
//       <GettingStartedNotice
//         counts={{
//           campaigns: campaigns.total,
//           events: events.total,
//           products: products.total,
//           assets: assets.total,
//         }}
//       />

//       {/* Stat cards */}
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
//         <StatCard
//           icon={<Megaphone className="w-6 h-6 text-blue-500" />}
//           title="Campaigns"
//           value={campaigns.total}
//           subtitle={`${campaigns.active} active`}
//           color="blue"
//         />
//         <StatCard
//           icon={<CalendarDays className="w-6 h-6 text-purple-500" />}
//           title="Events"
//           value={events.total}
//           subtitle={`${events.upcoming} upcoming`}
//           color="purple"
//         />
//         <StatCard
//           icon={<Package className="w-6 h-6 text-green-500" />}
//           title="Products"
//           value={products.total}
//           subtitle={`${products.active} active`}
//           color="green"
//         />
//         <StatCard
//           icon={<ImageIcon className="w-6 h-6 text-yellow-500" />}
//           title="Assets"
//           value={assets.total}
//           subtitle="Total assets"
//           color="yellow"
//         />
//       </div>

//       {/* Charts row */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
//         {/* Campaign growth */}
//         <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
//           <div className="flex items-center justify-between mb-4">
//             <div>
//               <h3 className="text-lg font-semibold text-gray-900">
//                 Campaign Growth
//               </h3>
//               <p className="text-sm text-gray-500">
//                 Campaigns created over the last 7 months
//               </p>
//             </div>
//             <TrendingUp className="w-5 h-5 text-green-500" />
//           </div>
//           <div className="relative h-64">
//             {campaignGrowth.length > 0 ? (
//               <svg className="w-full h-full" viewBox="0 0 600 250" preserveAspectRatio="none">
//                 {[0, 25, 50, 75, 100].map((v) => (
//                   <line
//                     key={v}
//                     x1="40"
//                     y1={200 - v * 1.8}
//                     x2="580"
//                     y2={200 - v * 1.8}
//                     stroke="#E5E7EB"
//                     strokeWidth="1"
//                     strokeDasharray="4 4"
//                     opacity="0.5"
//                   />
//                 ))}

//                 <polyline
//                   points={campaignGrowth
//                     .map((d, i) => {
//                       const x = 40 + (i / Math.max(campaignGrowth.length - 1, 1)) * 540;
//                       const y = 200 - (d.count / maxGrowth) * 190;
//                       return `${x},${y}`;
//                     })
//                     .join(" ")}
//                   fill="none"
//                   stroke="#3B82F6"
//                   strokeWidth="3"
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                 />

//                 {campaignGrowth.map((d, i) => {
//                   const x = 40 + (i / Math.max(campaignGrowth.length - 1, 1)) * 540;
//                   const y = 200 - (d.count / maxGrowth) * 190;
//                   return (
//                     <g key={i}>
//                       <circle
//                         cx={x}
//                         cy={y}
//                         r={hoveredPoint === i ? 8 : 5}
//                         fill="#3B82F6"
//                         stroke="white"
//                         strokeWidth="3"
//                         className="transition-all duration-200 cursor-pointer"
//                         onMouseEnter={() => setHoveredPoint(i)}
//                         onMouseLeave={() => setHoveredPoint(null)}
//                       />
//                       {hoveredPoint === i && (
//                         <>
//                           <rect x={x - 20} y={y - 35} width="40" height="24" rx="6" fill="white" stroke="#E5E7EB" />
//                           <text x={x} y={y - 18} textAnchor="middle" className="text-xs font-semibold fill-gray-700">
//                             {d.count}
//                           </text>
//                         </>
//                       )}
//                       <text x={x} y="220" textAnchor="middle" className="text-xs fill-gray-600 font-medium">
//                         {d.month}
//                       </text>
//                     </g>
//                   );
//                 })}
//               </svg>
//             ) : (
//               <div className="flex items-center justify-center h-full">
//                 <p className="text-gray-400">No data available</p>
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Campaign status breakdown */}
//         <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
//           <div className="flex items-center justify-between mb-4">
//             <div>
//               <h3 className="text-lg font-semibold text-gray-900">Campaign Status</h3>
//               <p className="text-sm text-gray-500">Active and archived</p>
//             </div>
//             <PieChart className="w-5 h-5 text-gray-400" />
//           </div>
//           <div className="space-y-4">
//             <StatusBar label="Active" value={campaigns.active} total={campaigns.total} color="#3B82F6" />
//             <StatusBar label="Archived" value={campaigns.archived} total={campaigns.total} color="#6B7280" />
//           </div>
//         </div>
//       </div>

//       {/* Products / Assets / Events row */}
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
//         <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
//           <div className="flex items-center justify-between mb-4">
//             <h3 className="text-lg font-semibold text-gray-900">Products</h3>
//             <Package className="w-5 h-5 text-gray-400" />
//           </div>
//           <StatusBar label="Active" value={products.active} total={products.total} color="#10B981" />
//           <div className="mt-3">
//             <StatusBar label="Inactive" value={products.inactive} total={products.total} color="#D1D5DB" />
//           </div>
//         </div>

//         <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
//           <div className="flex items-center justify-between mb-4">
//             <h3 className="text-lg font-semibold text-gray-900">Assets</h3>
//             <ImageIcon className="w-5 h-5 text-gray-400" />
//           </div>
//           <div className="flex items-center justify-center py-4">
//             <div className="text-center">
//               <p className="text-4xl font-bold text-gray-900">{assets.total}</p>
//               <p className="text-sm text-gray-500 mt-1">Total assets</p>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
//           <div className="flex items-center justify-between mb-4">
//             <h3 className="text-lg font-semibold text-gray-900">Events</h3>
//             <CalendarDays className="w-5 h-5 text-gray-400" />
//           </div>
//           <div className="flex items-center gap-3 mb-3">
//             <Clock className="w-4 h-4 text-blue-500" />
//             <span className="text-sm text-gray-600">Upcoming</span>
//             <span className="ml-auto text-sm font-semibold text-gray-900">{events.upcoming}</span>
//           </div>
//           <div className="flex items-center gap-3">
//             <CheckCircle2 className="w-4 h-4 text-gray-400" />
//             <span className="text-sm text-gray-600">Past</span>
//             <span className="ml-auto text-sm font-semibold text-gray-900">{events.past}</span>
//           </div>
//         </div>
//       </div>

//       {/* Recent campaigns */}
//       <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
//         <div className="flex items-center justify-between mb-4">
//           <div>
//             <h3 className="text-lg font-semibold text-gray-900">Recent Campaigns</h3>
//             <p className="text-sm text-gray-500">Latest updates for your tenant</p>
//           </div>
//           <Megaphone className="w-5 h-5 text-gray-400" />
//         </div>
//         <div className="space-y-3">
//           {recentCampaigns.length === 0 && (
//             <p className="text-gray-400 text-sm">No campaigns yet.</p>
//           )}
//           {recentCampaigns.map((c) => (
//             <div
//               key={c.campaign_id}
//               className="group flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-all duration-200 cursor-pointer"
//             >
//               <div>
//                 <p className="text-sm font-medium text-gray-800 group-hover:text-blue-600 transition-colors">
//                   {c.title}
//                 </p>
//                 <p className="text-xs text-gray-400">
//                   {c.start_date ? new Date(c.start_date).toLocaleDateString() : ""}
//                   {c.end_date ? ` – ${new Date(c.end_date).toLocaleDateString()}` : ""}
//                 </p>
//               </div>
//               <div className="flex items-center gap-3">
//                 <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600 capitalize">
//                   {c.status}
//                 </span>
//                 <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// // ---- Shared small components --------------------------------------------

// interface StatCardProps {
//   icon: React.ReactNode;
//   title: string;
//   value: number;
//   subtitle: string;
//   color: string;
// }

// const StatCard: React.FC<StatCardProps> = ({ icon, title, value, subtitle, color }) => {
//   const colorClasses: Record<string, string> = {
//     blue: "border-blue-500 hover:border-blue-600",
//     green: "border-green-500 hover:border-green-600",
//     purple: "border-purple-500 hover:border-purple-600",
//     yellow: "border-yellow-500 hover:border-yellow-600",
//   };

//   return (
//     <div
//       className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 ${colorClasses[color]} hover:scale-[1.02] group`}
//     >
//       <div className="flex items-center justify-between">
//         <div>
//           <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</p>
//           <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-105 transition-transform origin-left">
//             {value}
//           </p>
//           <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
//         </div>
//         <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl group-hover:scale-110 transition-transform duration-300 group-hover:shadow-lg">
//           {icon}
//         </div>
//       </div>
//     </div>
//   );
// };

// interface StatusBarProps {
//   label: string;
//   value: number;
//   total: number;
//   color: string;
// }

// const StatusBar: React.FC<StatusBarProps> = ({ label, value, total, color }) => {
//   const percentage = total > 0 ? (value / total) * 100 : 0;
//   return (
//     <div>
//       <div className="flex justify-between mb-1">
//         <span className="text-sm font-medium text-gray-700">{label}</span>
//         <span className="text-sm text-gray-500 font-medium">{value}</span>
//       </div>
//       <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
//         <div
//           className="h-2.5 rounded-full transition-all duration-500"
//           style={{ width: `${percentage}%`, backgroundColor: color, boxShadow: `0 0 10px ${color}40` }}
//         ></div>
//       </div>
//     </div>
//   );
// };

// // ---- Getting started banner -----------------------------------------------

// type SetupKey = "campaigns" | "events" | "products" | "assets";

// const SETUP_STEPS: Array<{
//   key: SetupKey;
//   label: string;
//   icon: React.ElementType;
//   title: string;
//   description: string;
//   color: string;
//   tint: string;
// }> = [
//     {
//       key: "campaigns",
//       label: "campaigns",
//       icon: Megaphone,
//       title: "Create a campaign",
//       description: "Set up your first campaign from the Campaign tab.",
//       color: "#2563EB",
//       tint: "#EFF6FF",
//     },
//     {
//       key: "events",
//       label: "events",
//       icon: CalendarDays,
//       title: "Schedule an event",
//       description: "Plan events and link them to your campaigns from the Events tab.",
//       color: "#7C3AED",
//       tint: "#F5F3FF",
//     },
//     {
//       key: "products",
//       label: "products",
//       icon: Package,
//       title: "Add your products",
//       description: "Register the products your campaigns will promote from the Products tab.",
//       color: "#16A34A",
//       tint: "#F0FDF4",
//     },
//     {
//       key: "assets",
//       label: "assets",
//       icon: ImageIcon,
//       title: "Upload assets",
//       description: "Bring in images, videos and documents from the Assets tab.",
//       color: "#D97706",
//       tint: "#FFFBEB",
//     },
//   ];

// /** Full class strings (not built dynamically) so Tailwind keeps them in the build. */
// const STEP_GRID: Record<number, string> = {
//   1: "grid-cols-1 sm:grid-cols-2",
//   2: "grid-cols-1 sm:grid-cols-2",
//   3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
//   4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
// };

// /** ["a"] -> "a", ["a","b"] -> "a or b", ["a","b","c"] -> "a, b or c" */
// const joinWithOr = (items: string[]) =>
//   items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;

// interface GettingStartedNoticeProps {
//   counts: Record<SetupKey, number>;
// }

// /**
//  * Getting-started banner. Each step hides once its count is above zero, and
//  * the whole banner hides once campaigns, events, products and assets all exist.
//  *
//  * Colours are inline styles on purpose: the global theme restyles Tailwind
//  * gradient classes and recolours the text inside them.
//  */
// const GettingStartedNotice: React.FC<GettingStartedNoticeProps> = ({ counts }) => {
//   const pendingSteps = SETUP_STEPS.filter((step) => (counts[step.key] ?? 0) <= 0);
//   const completedCount = SETUP_STEPS.length - pendingSteps.length;

//   // Everything is set up, so there is nothing left to show.
//   if (pendingSteps.length === 0) return null;

//   const isFreshWorkspace = completedCount === 0;
//   const missingList = joinWithOr(pendingSteps.map((step) => step.label));
//   const progressPct = Math.round((completedCount / SETUP_STEPS.length) * 100);

//   return (
//     <div
//       className="mb-8 p-6 rounded-2xl shadow-lg"
//       style={{
//         background: "linear-gradient(120deg, #0A0A0A 0%, #064E2B 55%, #16A34A 100%)",
//       }}
//     >
//       {/* Heading + progress */}
//       <div className="flex items-start justify-between gap-4 flex-wrap">
//         <div className="flex items-start gap-3 min-w-0 flex-1">
//           <div
//             className="p-2.5 rounded-xl flex-shrink-0"
//             style={{
//               backgroundColor: "rgba(255, 255, 255, 0.12)",
//               border: "1px solid rgba(255, 255, 255, 0.22)",
//             }}
//           >
//             <Sparkles className="w-5 h-5" style={{ color: "#FFFFFF" }} />
//           </div>
//           <div className="min-w-0">
//             <h2 className="text-lg font-semibold" style={{ color: "#FFFFFF" }}>
//               {isFreshWorkspace
//                 ? "Your workspace is ready. Let's get started."
//                 : "Good progress. Let's finish setting up your workspace."}
//             </h2>
//             <p
//               className="text-sm mt-1 max-w-3xl leading-relaxed"
//               style={{ color: "rgba(255, 255, 255, 0.82)" }}
//             >
//               There are no {missingList} in your workspace yet.{" "}
//               {isFreshWorkspace
//                 ? "Once your team starts adding content, this dashboard will automatically track campaign growth, status and recent activity."
//                 : `Complete the remaining ${pendingSteps.length === 1 ? "step" : "steps"} below to see the full picture across campaigns, events, products and assets.`}
//             </p>
//           </div>
//         </div>

//         {!isFreshWorkspace && (
//           <div className="flex-shrink-0 min-w-[150px]">
//             <p className="text-xs font-semibold mb-1" style={{ color: "#FFFFFF" }}>
//               {completedCount} of {SETUP_STEPS.length} completed
//             </p>
//             <div
//               className="h-1.5 rounded-full overflow-hidden"
//               style={{ backgroundColor: "rgba(255, 255, 255, 0.25)" }}
//             >
//               <div
//                 className="h-full rounded-full transition-all duration-500"
//                 style={{ width: `${progressPct}%`, backgroundColor: "#FFFFFF" }}
//               />
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Remaining steps only */}
//       <div className={`grid gap-4 mt-5 ${STEP_GRID[pendingSteps.length]}`}>
//         {pendingSteps.map((step, index) => {
//           const Icon = step.icon;
//           return (
//             <div
//               key={step.key}
//               className="flex items-start gap-3 p-4 rounded-xl shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
//               style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB" }}
//             >
//               <div
//                 className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
//                 style={{ backgroundColor: step.tint }}
//               >
//                 <Icon className="w-5 h-5" style={{ color: step.color }} />
//               </div>
//               <div className="min-w-0">
//                 <p
//                   className="text-[10px] font-semibold uppercase tracking-wider"
//                   style={{ color: step.color }}
//                 >
//                   Step {index + 1}
//                 </p>
//                 <p className="text-sm font-semibold mt-0.5" style={{ color: "#111827" }}>
//                   {step.title}
//                 </p>
//                 <p className="text-xs mt-0.5 leading-snug" style={{ color: "#4B5563" }}>
//                   {step.description}
//                 </p>
//               </div>
//             </div>
//           );
//         })}
//       </div>
//     </div>
//   );
// };

// export default CustomerManagerDashboard;

// CustomerManagerDashboard.tsx
// CustomerManagerDashboard.tsx
import React, { useState } from "react";
import { Library, FolderCog, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Where each card goes when clicked. Change these paths if your routes differ.
const LIBRARY_PATH = "/customer-manager/library";
const ASSET_MANAGEMENT_PATH = "/customer-manager/asset-management";

interface Palette {
  iconBg: string;
  iconShadow: string;
  borderHover: string;
  shadowHover: string;
}

// Colours are inline styles on purpose: the global theme restyles Tailwind
// colour and hover classes, which can make the cards look grey.
const PALETTES: Record<"blue" | "navy", Palette> = {
  blue: {
    iconBg: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
    iconShadow: "0 12px 24px -10px rgba(37, 99, 235, 0.7)",
    borderHover: "#93C5FD",
    shadowHover: "0 24px 48px -16px rgba(37, 99, 235, 0.35)",
  },
  navy: {
    iconBg: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
    iconShadow: "0 12px 24px -10px rgba(15, 23, 42, 0.7)",
    borderHover: "#94A3B8",
    shadowHover: "0 24px 48px -16px rgba(15, 23, 42, 0.3)",
  },
};

interface ModeCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  ctaLabel: string;
  variant: "blue" | "navy";
  onClick: () => void;
}

const ModeCard: React.FC<ModeCardProps> = ({
  icon,
  title,
  description,
  ctaLabel,
  variant,
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);
  const p = PALETTES[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      className="flex flex-col justify-between text-left rounded-3xl p-8 w-full transition-all duration-300 focus:outline-none"
      style={{
        // White card with a soft blue glow in the top-right corner
        background:
          "radial-gradient(circle at 100% 0%, rgba(147, 197, 253, 0.28) 0%, rgba(255, 255, 255, 0) 55%), #FFFFFF",
        border: `1px solid ${hovered ? p.borderHover : "#D5DEEA"}`,
        boxShadow: hovered
          ? p.shadowHover
          : "0 6px 20px -10px rgba(15, 23, 42, 0.18)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        minHeight: 200,
      }}
    >
      {/* Icon + text */}
      <span className="flex items-start gap-5">
        <span
          className="flex flex-shrink-0 items-center justify-center rounded-2xl transition-transform duration-300"
          style={{
            width: 64,
            height: 64,
            background: p.iconBg,
            color: "#FFFFFF",
            boxShadow: p.iconShadow,
            transform: hovered ? "scale(1.06)" : "scale(1)",
          }}
        >
          {icon}
        </span>

        <span className="min-w-0 pt-0.5">
          <span
            className="block text-2xl font-semibold tracking-tight"
            style={{ color: "#0F172A" }}
          >
            {title}
          </span>
          <span
            className="mt-1.5 block text-base leading-relaxed"
            style={{ color: "#64748B" }}
          >
            {description}
          </span>
        </span>
      </span>

      {/* Call to action */}
      <span
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium"
        style={{ color: "#2563EB" }}
      >
        {ctaLabel}
        <ArrowRight
          className="w-4 h-4 transition-transform duration-300"
          style={{ transform: hovered ? "translateX(5px)" : "translateX(0)" }}
        />
      </span>
    </button>
  );
};

const CustomerManagerDashboard: React.FC = () => {
  const navigate = useNavigate();

  return (
    // The header is roughly 90px tall, so this centres the content in the space below it.
    <div
  className="relative flex items-center justify-center px-4 overflow-hidden"
  style={{ height: "calc(100dvh - 148px)" }}
>
      {/* Soft decorative glow behind the content */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(191, 219, 254, 0.55) 0%, rgba(255, 255, 255, 0) 60%)",
        }}
      />

      <div className="relative w-full max-w-6xl">
        <div className="text-center mb-8">
          <h2
            className="text-4xl md:text-5xl font-bold tracking-tight"
            style={{ color: "#0F172A" }}
          >
            What would you like{" "}
            <span
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #0F172A 0%, var(--color-primary, #2563EB) 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
              }}
            >
              to do?
            </span>
          </h2>
          <p className="mt-3 text-lg" style={{ color: "#64748B" }}>
            Choose a workspace to get started
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ModeCard
            variant="blue"
            icon={<Library className="w-8 h-8" />}
            title="Library"
            description="Browse and link Events, Products and Assets"
            ctaLabel="Open the library"
            onClick={() => navigate(LIBRARY_PATH)}
          />
          <ModeCard
            variant="navy"
            icon={<FolderCog className="w-8 h-8" />}
            title="Asset Management"
            description="Upload, fix, archive, share and delete"
            ctaLabel="Manage assets"
            onClick={() => navigate(ASSET_MANAGEMENT_PATH)}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerManagerDashboard;
